import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSafeQuery } from '@/common/hooks/useSafeQuery';
import {
    Workspace,
    WorkspaceWithDetail,
    WorkspaceWithInfo,
} from '@tickitacka/shared';
import { useAuthStore } from '@/stores/authStore';
import { workspaceService } from './workspaceService';
import { useWorkspaceStore } from '../hooks';
import { queryClient } from '@/queryClient';

// 내 워크스페이스 조회 훅
export function useMyWorkspaces() {
    return useSafeQuery<WorkspaceWithInfo[], Error>({
        queryKey: ['workspace'],
        queryFn: () => workspaceService.getMyWorkspaces(),
    });
}

// 단일 워크스페이스 조회 훅
export function useWorkspace(workspaceId: string) {
    return useSafeQuery<WorkspaceWithInfo, Error>({
        queryKey: ['workspace', workspaceId],
        queryFn: () => workspaceService.getWorkspaceById(workspaceId),
        enabled: !!workspaceId,
        retry: false,
    });
}

// 워크스페이스의 상세 정보 조회 훅
export function useWorkspaceDetails(workspaceId: string) {
    return useSafeQuery<WorkspaceWithDetail, Error>({
        queryKey: ['workspace', workspaceId, 'detail'],
        queryFn: () => workspaceService.getWorkspaceDetailsById(workspaceId),
        enabled: !!workspaceId,
    });
}

// 워크스페이스 생성 뮤테이션 훅
export function useCreateWorkspace() {
    const queryClient = useQueryClient();

    return useMutation<
        Workspace,
        Error,
        {
            name: string;
            description?: string;
            profileImageUrl?: string;
        }
    >({
        mutationFn: ({ name, description, profileImageUrl }) =>
            workspaceService.createWorkspace(
                name,
                description,
                profileImageUrl
            ),
        onSuccess: async () => {
            // 워크스페이스 목록과 내 워크스페이스 목록 캐시 무효화
            await queryClient.invalidateQueries({ queryKey: ['workspace'] });
        },
    });
}

// 워크스페이스 업데이트 뮤테이션 훅
export function useUpdateWorkspace() {
    const queryClient = useQueryClient();
    const updateWorkspaceInList = useWorkspaceStore(
        state => state.updateWorkspaceInList
    );

    return useMutation<
        Workspace,
        Error,
        {
            workspaceId: string;
            name?: string;
            description?: string;
            profileImageUrl?: string;
        }
    >({
        mutationFn: ({ workspaceId, name, description, profileImageUrl }) =>
            workspaceService.updateWorkspace(
                workspaceId,
                name,
                description,
                profileImageUrl
            ),
        onSuccess: async updatedWorkspace => {
            void updateWorkspaceInList(updatedWorkspace);

            // 개별 워크스페이스, 내 워크스페이스 목록 캐시 무효화
            await queryClient.invalidateQueries({ queryKey: ['workspace'] });
        },
    });
}

// 워크스페이스 삭제 뮤테이션 훅
export function useDeleteWorkspace() {
    const queryClient = useQueryClient();

    return useMutation<string, Error, string>({
        mutationFn: workspaceId =>
            workspaceService.deleteWorkspace(workspaceId),
        onSuccess: async () => {
            // 내 워크스페이스 목록 캐시 무효화
            await queryClient.invalidateQueries({ queryKey: ['workspace'] });
        },
    });
}

// 워크스페이스 멤버 조회 훅
export function useGetWorkspaceMembers(workspaceId: string) {
    return useSafeQuery({
        queryKey: ['workspace', workspaceId, 'detail', 'members'],
        queryFn: () => workspaceService.getWorkspaceMembers(workspaceId),
        enabled: !!workspaceId,
    });
}

// 워크스페이스 멤버 삭제 뮤테이션 훅
export function useRemoveWorkspaceMember() {
    const queryClient = useQueryClient();

    return useMutation<
        boolean,
        Error,
        {
            workspaceId: string;
            userId: string;
        }
    >({
        mutationFn: ({ workspaceId, userId }) =>
            workspaceService.removeWorkspaceMember(workspaceId, userId),
        onSuccess: async (_, variables) => {
            // 워크스페이스 상세 정보 캐시 무효화
            await queryClient.invalidateQueries({
                queryKey: ['workspace', variables.workspaceId, 'detail'],
            });
        },
    });
}

// 워크스페이스 멤버 권한 업데이트 뮤테이션 훅
export function useUpdateWorkspaceMemberRole() {
    const queryClient = useQueryClient();

    return useMutation<
        boolean,
        Error,
        {
            workspaceId: string;
            userId: string;
            role: string;
        }
    >({
        mutationFn: ({ workspaceId, userId, role }) =>
            workspaceService.updateWorkspaceMemberRole(
                workspaceId,
                userId,
                role
            ),
        onSuccess: async (_, variables) => {
            // 워크스페이스 상세 정보 캐시 무효화
            await queryClient.invalidateQueries({
                queryKey: ['workspace', variables.workspaceId, 'detail'],
            });
        },
    });
}

/**
 * 워크스페이스 오너 권한 양도 뮤테이션 훅
 */
export function useTransferWorkspaceOwnership() {
    const { user } = useAuthStore();

    return useMutation<
        boolean,
        Error,
        {
            workspaceId: string;
            newOwnerId: string;
        }
    >({
        mutationFn: ({ workspaceId, newOwnerId }) => {
            if (!user?.id) {
                throw new Error('User not authenticated');
            }
            return workspaceService.transferWorkspaceOwnership(
                workspaceId,
                user.id,
                newOwnerId
            );
        },
        onSuccess: async (_, variables) => {
            // 워크스페이스 상세 정보 캐시 무효화
            await queryClient.invalidateQueries({
                queryKey: ['workspace', variables.workspaceId, 'detail'],
            });
        },
    });
}

/**
 * 팀 ID를 기반으로 사용자가 해당 팀이 속한 워크스페이스의 멤버인지 확인하는 훅
 * 팀 -> 워크스페이스 관계를 확인하고 현재 사용자가 해당 워크스페이스 멤버인지 검사
 * @param teamId 확인할 팀 ID
 */
export function useCheckWorkspaceMembership(
    teamId: string | undefined,
    userId?: string
) {
    const { user } = useAuthStore();
    return useQuery({
        queryKey: ['workspace', 'team', teamId, 'membership'],
        queryFn: () => {
            const targetUserId = userId || user?.id;
            if (!teamId || !targetUserId) {
                return {
                    isAllowed: true,
                };
            }

            return workspaceService.checkUserWorkspaceMembership(
                teamId,
                targetUserId
            );
        },
    });
}

/**
 * 목표 ID를 통해 해당 목표가 속한 팀이 포함된 워크스페이스 정보를 불러오는 훅 함수
 * @param goalId 조회할 목표 ID
 */
export function useWorkspaceByGoalId(goalId: string | undefined) {
    return useSafeQuery<WorkspaceWithInfo | null, Error>({
        queryKey: ['workspace', 'goal', goalId],
        queryFn: async () => {
            if (!goalId) return null;
            return workspaceService.getWorkspaceByGoalId(goalId);
        },
        enabled: !!goalId,
    });
}

/**
 * 두 사용자가 같은 워크스페이스의 멤버인지 확인하는 훅 함수
 * @param userId1 첫 번째 사용자 ID
 * @param userId2 두 번째 사용자 ID
 * @returns 같은 워크스페이스 멤버인지 여부
 */
export function useCheckSameWorkspaceMembership(
    userId1: string | undefined,
    userId2: string | undefined
) {
    return useQuery<
        { isSameWorkspace: boolean; workspaceIds?: string[]; error?: string },
        Error
    >({
        queryKey: ['workspace', 'users', 'same-membership', userId1, userId2],
        queryFn: async () => {
            if (!userId1 || !userId2) {
                return { isSameWorkspace: false };
            }

            // workspaceService의 함수를 사용하여 같은 워크스페이스 멤버인지 확인
            return await workspaceService.checkSameWorkspaceMembership(
                userId1,
                userId2
            );
        },
        enabled: !!userId1 && !!userId2,
    });
}
